import React, { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { TaskyAlert, TaskyButton, TaskyDialog, TaskyInput, TaskySelect, TaskySpinner } from '../tasky';
import adminVideoStyleRecipeService, {
  type TrialProductOption,
  type VideoStyleTrialDetail,
  videoStyleErrorMessages,
} from '../../services/api/adminVideoStyleRecipeService';

const SEARCH_DELAY_MS = 300;

interface Props {
  recipe: { id: number; name: string } | null;
  onClose: () => void;
  onStarted: (trial: VideoStyleTrialDetail) => void;
}

/**
 * 「試しに作る」: 全組織の商品から1つ選び（訴求は任意）、見積もりを見て試作を始める。
 * 組織のクレジットは使わない。試作は全体で同時に1本まで（走っていれば開始できない）。
 */
const VideoStyleTrialDialog: React.FC<Props> = ({ recipe, onClose, onStarted }) => {
  const [query, setQuery] = useState('');
  const [products, setProducts] = useState<TrialProductOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [product, setProduct] = useState<TrialProductOption | null>(null);
  const [appeals, setAppeals] = useState<{ name: string; direction: string | null }[]>([]);
  const [appeal, setAppeal] = useState('');
  const [estimate, setEstimate] = useState<number | null>(null);
  const [runningTrialId, setRunningTrialId] = useState<number | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [isStarting, setIsStarting] = useState(false);

  const recipeId = recipe?.id ?? null;

  // 開いたら状態を戻し、見積もりと走っている試作を取る
  useEffect(() => {
    if (recipeId === null) return;
    setQuery('');
    setProduct(null);
    setAppeals([]);
    setAppeal('');
    setErrors([]);
    setEstimate(null);
    setRunningTrialId(null);
    let cancelled = false;
    adminVideoStyleRecipeService
      .trials(recipeId)
      .then((list) => {
        if (cancelled) return;
        setEstimate(list.estimatedCredits);
        setRunningTrialId(list.runningTrialId);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [recipeId]);

  // 入力が止まってから検索する（後から返った古い結果で上書きしない）
  useEffect(() => {
    if (recipeId === null) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setIsSearching(true);
      adminVideoStyleRecipeService
        .searchProducts(query.trim())
        .then((rows) => {
          if (!cancelled) setProducts(rows);
        })
        .catch(() => {
          if (!cancelled) setErrors(['商品を検索できませんでした。']);
        })
        .finally(() => {
          if (!cancelled) setIsSearching(false);
        });
    }, SEARCH_DELAY_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query, recipeId]);

  const choose = async (option: TrialProductOption) => {
    setProduct(option);
    setAppeal('');
    setAppeals([]);
    try {
      setAppeals(await adminVideoStyleRecipeService.productAppeals(option.id));
    } catch {
      setAppeals([]);
    }
  };

  const start = async () => {
    if (!recipe || !product) return;
    setIsStarting(true);
    setErrors([]);
    try {
      onStarted(await adminVideoStyleRecipeService.startTrial(recipe.id, { product_id: product.id, appeal: appeal || null }));
    } catch (error) {
      setErrors(videoStyleErrorMessages(error, '試作を始められませんでした。'));
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <TaskyDialog
      open={recipe !== null}
      onClose={() => !isStarting && onClose()}
      title={recipe ? `「${recipe.name}」を試しに作る` : ''}
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <TaskyButton variant="ghost" onClick={onClose} disabled={isStarting}>
            やめる
          </TaskyButton>
          <TaskyButton onClick={() => void start()} disabled={isStarting || !product || runningTrialId !== null}>
            {isStarting ? '開始中…' : '試作を始める'}
          </TaskyButton>
        </div>
      }
    >
      <div className="flex flex-col gap-3" data-testid="video-style-trial-dialog">
        {runningTrialId !== null && <TaskyAlert tone="warning">試作中のものがあります（#{runningTrialId}）。終わってから試してください。</TaskyAlert>}
        {errors.length > 0 && (
          <TaskyAlert tone="error">
            {errors.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </TaskyAlert>
        )}

        <label className="flex flex-col gap-1 text-sm font-medium">
          商品（全組織から名前か ID で検索）
          <span className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden />
            <TaskyInput value={query} onChange={(e) => setQuery(e.target.value)} placeholder="商品名 / 商品ID" className="pl-9" data-testid="video-style-trial-search" />
          </span>
        </label>

        <div className="flex max-h-56 flex-col gap-1 overflow-y-auto rounded-tasky-sm border border-warm-border p-1" role="listbox" aria-label="商品">
          {isSearching && products.length === 0 ? (
            <TaskySpinner />
          ) : products.length === 0 ? (
            <p className="p-3 text-sm text-gray-500">見つかりません。</p>
          ) : (
            products.map((option) => (
              <TaskyButton
                key={option.id}
                variant={product?.id === option.id ? 'secondary' : 'ghost'}
                size="sm"
                className="h-auto justify-start py-2 text-left"
                role="option"
                aria-selected={product?.id === option.id}
                onClick={() => void choose(option)}
              >
                <span className="flex flex-col items-start">
                  <span className="font-semibold text-dark">{option.name}</span>
                  <span className="text-xs font-normal text-gray-500">
                    #{option.id}・{option.organization_name ?? '組織なし'}
                  </span>
                </span>
              </TaskyButton>
            ))
          )}
        </div>

        {product && (
          <label className="flex flex-col gap-1 text-sm font-medium">
            訴求（任意）
            <TaskySelect value={appeal} onChange={(e) => setAppeal(e.target.value)} data-testid="video-style-trial-appeal">
              <option value="">指定しない（商品の情報から決める）</option>
              {appeals.map((a) => (
                <option key={a.name} value={a.name}>
                  {a.name}
                </option>
              ))}
            </TaskySelect>
          </label>
        )}

        <p className="text-sm text-gray-600" data-testid="video-style-trial-estimate">
          {estimate !== null ? `実費の目安: 約 ${estimate.toLocaleString()} クレジット相当（組織のクレジットからは引きません）` : '実費の目安を取得中…'}
        </p>
      </div>
    </TaskyDialog>
  );
};

export default VideoStyleTrialDialog;
